/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type RefObject, useEffect } from 'react';

/**
 * Gap left above the sheet when it is lifted clear of the on-screen keyboard. Matches vaul's own
 * `WINDOW_TOP_OFFSET`, so a corrected sheet lands exactly where vaul's happy path put it.
 */
const TOP_GAP = 26;

/** Types that render as a text-entry field, i.e. the ones that summon the on-screen keyboard. */
const NON_TEXT_INPUT_TYPES = new Set([
    'checkbox',
    'radio',
    'range',
    'color',
    'file',
    'image',
    'button',
    'submit',
    'reset',
]);

function isTextEntry(node: Element | null): boolean {
    if (node instanceof HTMLTextAreaElement) return true;
    if (node instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(node.type);
    return node instanceof HTMLElement && node.isContentEditable;
}

/**
 * Keeps a bottom sheet usable while the iOS on-screen keyboard is up (UCR-2984). Two independent bugs,
 * both verified on an iPhone 16 — see `ai-docs/header/doc.md` → "iOS keyboard" for the full history.
 *
 * ### 1. The sheet's geometry
 *
 * vaul repositions the sheet itself, but its handler is a *stateful flip-flop*: it toggles an internal
 * `keyboardIsOpen` ref on any visual-viewport delta over 60px and only runs while
 * `isInput(document.activeElement) || keyboardIsOpen.current`. Dismissing the keyboard blurs the field,
 * so partway through the close animation both halves of that gate go false, every remaining frame
 * returns early, and the inline `bottom`/`height` it wrote for the keyboard-up state stay applied — the
 * sheet is left floating with an empty gap beneath it.
 *
 * So this owns `bottom`/`height`/`max-height` instead, and is deliberately **stateless**: the values are
 * a pure function of the *current* viewport, never of what the keyboard did before, so no sequence of
 * events can strand it. It writes in a `requestAnimationFrame` scheduled off the same events vaul
 * listens to, landing after vaul's synchronous handler but still before paint, so vaul's value is
 * overwritten rather than rendered.
 *
 * `repositionInputs` is intentionally left enabled on `Drawer.Root` even though it drives that handler:
 * vaul gates `usePreventScroll` on it too (`vaul/dist/index.mjs` line 940).
 *
 * ### 2. The page scrolling when the field is tapped
 *
 * WebKit's behaviour on focusing an input is to scroll it to the **centre of the visual viewport** — not
 * merely into view — which both pans the visual viewport and scrolls the document, dragging
 * `position: fixed` elements and the page behind the sheet along with it. `preventScroll: true` is the
 * standard opt-out.
 *
 * This is why opening the sheet was always fine while *tapping the field afterwards* was not: the
 * opening focus comes from Radix's `FocusScope`, which calls `focus({ preventScroll: true })`
 * (`@radix-ui/react-focus-scope/dist/index.mjs` line 175). A native tap has no such option. Measured
 * on-device at 393×659 with a 223px keyboard — opening: `offsetTop` 0 / `scrollY` 0; tapping the field:
 * `offsetTop` 223 / `scrollY` 265.
 *
 * The fix routes a tap through the same option: suppress the native focus, then focus with the flag set.
 *
 * Two things that look like fixes for this and are **not** — both measured, don't spend time on them:
 * lifting the sheet so the field already clears the keyboard (WebKit wants it *centred*, so it pans
 * anyway), and any CSS scroll lock or `interactive-widget=resizes-content` (a pan is not document
 * scroll, and the document scrolled even with `overflow: hidden` applied).
 *
 * @param ref     the `Drawer.Content` element.
 * @param enabled whether the sheet is open — listeners are only attached while it is.
 */
export function useKeyboardInset(ref: RefObject<HTMLElement | null>, enabled: boolean): void {
    useEffect(() => {
        const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!enabled || !viewport) return;

        let frame = 0;

        const apply = () => {
            frame = 0;
            const element = ref.current;
            if (!element) return;

            // How much of the layout viewport the visual viewport no longer covers — i.e. the keyboard.
            // iOS shrinks only the visual viewport for it, which is why no `vh`/`dvh` length can express
            // this and it has to be measured. `offsetTop` is subtracted because a panned viewport has
            // already moved the sheet's fixed anchor by that much.
            const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);

            element.style.bottom = `${inset}px`;
            // Always drop vaul's inline height: the sheet is sized by its content against the max-height
            // below, so a stale fixed height can't survive a keyboard close.
            element.style.height = '';
            // Inline beats the `max-h-[67dvh]` class while the keyboard is up, and clearing it hands the
            // cap straight back when the keyboard goes away.
            element.style.maxHeight = inset > 0 ? `${viewport.height - TOP_GAP}px` : '';
        };

        const schedule = () => {
            frame ||= requestAnimationFrame(apply);
        };

        /**
         * Focus a tapped field the way Radix does on open — with `preventScroll` — so WebKit doesn't
         * centre it and drag the page along. `preventDefault()` suppresses the native focus (and with it
         * the centring scroll); the manual `focus()` stays inside the user gesture, which is what iOS
         * requires to raise the keyboard at all.
         *
         * Registered `{ passive: false }` so `preventDefault()` is honoured.
         *
         * Known trade-off: suppressing the native focus means the tap no longer places the text caret at
         * the tapped position — it lands at the end instead. Invisible on an empty search field, and the
         * same trade react-aria makes in `preventScrollMobileSafari`.
         */
        const onTouchEndFocusWithoutScroll = (event: Event) => {
            const element = ref.current;
            if (!element) return;
            const target = event.target as Element | null;
            if (!isTextEntry(target) || !element.contains(target)) return;
            // Only a tap that *changes* focus triggers the centring scroll. Once the field holds focus
            // and the keyboard is already up there is nothing to centre, and intercepting anyway would
            // suppress native caret placement for every tap — i.e. the user could never tap to reposition
            // the cursor inside their own query. Same guard shape as vaul's (`index.mjs` line 219).
            if (target === document.activeElement) return;

            event.preventDefault();
            (target as HTMLElement).focus({ preventScroll: true });
        };

        /**
         * Last resort, mirroring react-aria: if the document scrolls anyway, put it back. Measured at
         * `scrollY` 265 on-device during the bug, so this guards a real case, not a theoretical one.
         *
         * **Gated on vaul having pinned the body, and that gate is load-bearing.** Scrolling to `0` is
         * only correct while `body` is `position: fixed` with a compensating negative offset — then 0 *is*
         * the user's position. vaul applies that on iOS only (`usePositionFixed` is gated on `isSafari()`,
         * false in desktop Chrome and on Android Chrome), so off-iOS `scrollY` holds a real value and
         * resetting it would throw the user to the top of the page — for any scroll event at all, with the
         * damage outliving the sheet. Measured before this guard: open the sheet at `scrollY` 600, one
         * scroll event, and the user ends up at 0.
         */
        const onWindowScroll = () => {
            if (document.body.style.position !== 'fixed') return;
            if (window.scrollY !== 0) window.scrollTo(0, 0);
        };

        viewport.addEventListener('resize', schedule);
        viewport.addEventListener('scroll', schedule);
        document.addEventListener('touchend', onTouchEndFocusWithoutScroll, {
            capture: true,
            passive: false,
        });
        window.addEventListener('scroll', onWindowScroll, { passive: true });
        schedule();

        return () => {
            if (frame) cancelAnimationFrame(frame);
            viewport.removeEventListener('resize', schedule);
            viewport.removeEventListener('scroll', schedule);
            document.removeEventListener('touchend', onTouchEndFocusWithoutScroll, { capture: true });
            window.removeEventListener('scroll', onWindowScroll);
        };
    }, [ref, enabled]);
}
